import { useMutation, useQueryClient } from 'react-query'
import {
  CreateDiverDto,
  CreateFindingDto,
  CreateInteractiveSchemaReportDto,
  DiverDto,
  InteractiveSchema,
  InteractiveSchemaReport,
  InteractiveSchemaReportFinding,
  UpdateDiverDto,
  UpdateFindingDto,
  UpdateInteractiveSchemaReportDto,
} from './interactive-schema-types'
import * as http from './interactive-schema-http'
import { useGet } from './reactQuery'

export const useGetInteractiveSchemas = (customerId: number, activeOnly = true) =>
  useGet<InteractiveSchema[]>(`customers/${customerId}/interactive-schemas`, { activeOnly }, {
    queryKey: ['interactive-schemas', customerId, String(activeOnly)],
    enabled: !!customerId,
  })

export const useGetInteractiveSchemasByEquipmentType = (customerId: number, equipmentTypeId: number) =>
  useGet<InteractiveSchema[]>(
    `customers/${customerId}/interactive-schemas/by-equipment-type/${equipmentTypeId}`,
    undefined,
    {
      queryKey: ['interactive-schemas', customerId, 'equipment-type', equipmentTypeId],
      enabled: !!customerId && !!equipmentTypeId,
    },
  )

export const useGetInteractiveSchemaById = (customerId: number, id: number) =>
  useGet<InteractiveSchema>(`customers/${customerId}/interactive-schemas/${id}`, undefined, {
    queryKey: ['interactive-schema', customerId, id],
    enabled: !!customerId && !!id,
  })

export const useCreateInteractiveSchema = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ customerId, data }: { customerId: number; data: any }) =>
        http.createInteractiveSchema(customerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-schemas'] })
    },
  })
}

export const useUpdateInteractiveSchema = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ customerId, id, data }: { customerId: number; id: number; data: any }) =>
        http.updateInteractiveSchema(customerId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-schemas'] })
      queryClient.invalidateQueries({ queryKey: ['interactive-schema'] })
    },
  })
}

export const useDeleteInteractiveSchema = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ customerId, id }: { customerId: number; id: number }) =>
        http.deleteInteractiveSchema(customerId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-schemas'] })
    },
  })
}

export const useGetInteractiveSchemaReportById = (id: number) =>
  useGet<InteractiveSchemaReport>(`interactive-schema-reports/${id}`, undefined, {
    queryKey: ['interactive-schema-report', id],
    enabled: !!id,
  })

export const useGetReportsByEquipmentId = (equipmentId: number) =>
  useGet<InteractiveSchemaReport[]>(`interactive-schema-reports/equipment/${equipmentId}`, undefined, {
    queryKey: ['interactive-schema-reports', 'equipment', equipmentId],
    enabled: !!equipmentId,
  })

export const useGetReportsBySchemaId = (schemaId: number) =>
  useGet<InteractiveSchemaReport[]>(`interactive-schema-reports/schema/${schemaId}`, undefined, {
    queryKey: ['interactive-schema-reports', 'schema', schemaId],
    enabled: !!schemaId,
  })

export const useCreateInteractiveSchemaReport = () => {
  const queryClient = useQueryClient()
  return useMutation<InteractiveSchemaReport, Error, CreateInteractiveSchemaReportDto>({
    mutationFn: dto => http.createInteractiveSchemaReport(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-reports'] })
    },
  })
}

export const useUpdateInteractiveSchemaReport = (reportId: number) => {
  const queryClient = useQueryClient()
  return useMutation<InteractiveSchemaReport, Error, UpdateInteractiveSchemaReportDto>({
    mutationFn: dto => http.updateInteractiveSchemaReport(reportId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-report', reportId] })
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-reports'] })
    },
  })
}

export const useUpdateReportData = (reportId: number) => {
  const queryClient = useQueryClient()
  return useMutation<boolean, Error, string>({
    mutationFn: reportDataJson => http.updateReportData(reportId, reportDataJson),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-report', reportId] })
    },
  })
}

export const useUpdateReportStatus = (reportId: number) => {
  const queryClient = useQueryClient()
  return useMutation<boolean, Error, number>({
    mutationFn: status => http.updateReportStatus(reportId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-report', reportId] })
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-reports'] })
    },
  })
}

export const useCompleteReport = (reportId: number) => {
  const queryClient = useQueryClient()
  return useMutation<boolean, Error>({
    mutationFn: () => http.completeReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-report', reportId] })
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-reports'] })
    },
  })
}

export const useDeleteReport = () => {
  const queryClient = useQueryClient()
  return useMutation<boolean, Error, number>({
    mutationFn: reportId => http.deleteReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-reports'] })
    },
  })
}

export const useGetFindingsByReportId = (reportId: number) =>
  useGet<InteractiveSchemaReportFinding[]>(`interactive-schema-reports/${reportId}/findings`, undefined, {
    queryKey: ['interactive-schema-findings', reportId],
    enabled: !!reportId,
  })

export const useAddFinding = (reportId: number) => {
  const queryClient = useQueryClient()
  return useMutation<InteractiveSchemaReportFinding, Error, CreateFindingDto>({
    mutationFn: dto => http.addFinding(reportId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-findings', reportId] })
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-report', reportId] })
    },
  })
}

export const useUpdateFinding = (reportId: number, findingId: number) => {
  const queryClient = useQueryClient()
  return useMutation<InteractiveSchemaReportFinding, Error, UpdateFindingDto>({
    mutationFn: dto => http.updateFinding(reportId, findingId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-findings', reportId] })
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-report', reportId] })
    },
  })
}

export const useDeleteFinding = (reportId: number) => {
  const queryClient = useQueryClient()
  return useMutation<boolean, Error, number>({
    mutationFn: findingId => http.deleteFinding(reportId, findingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-findings', reportId] })
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-report', reportId] })
    },
  })
}

export const useAddFindings = (reportId: number) => {
  const queryClient = useQueryClient()
  return useMutation<InteractiveSchemaReportFinding[], Error, CreateFindingDto[]>({
    mutationFn: dtos => http.addFindings(reportId, dtos),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-findings', reportId] })
      queryClient.invalidateQueries({ queryKey: ['interactive-schema-report', reportId] })
    },
  })
}

export const useGetDivers = (customerId: number) =>
  useGet<DiverDto[]>(`customers/${customerId}/divers`, undefined, {
    queryKey: ['divers', customerId],
    enabled: !!customerId,
  })

export const useCreateDiver = (customerId: number) => {
  const queryClient = useQueryClient()
  return useMutation<DiverDto, Error, CreateDiverDto>({
    mutationFn: dto => http.createDiver(customerId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['divers', customerId] })
    },
  })
}

export const useUpdateDiver = (customerId: number) => {
  const queryClient = useQueryClient()
  return useMutation<DiverDto, Error, { id: number } & UpdateDiverDto>({
    mutationFn: ({ id, ...dto }) => http.updateDiver(customerId, id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['divers', customerId] })
    },
  })
}

export const useDeleteDiver = (customerId: number) => {
  const queryClient = useQueryClient()
  return useMutation<boolean, Error, number>({
    mutationFn: diverId => http.deleteDiver(customerId, diverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['divers', customerId] })
    },
  })
}

export const useGetReportsByMainComponentId = (mainComponentId: number) =>
  useGet<InteractiveSchemaReport[]>(
    `interactive-schema-reports/by-main-component/${mainComponentId}`,
    undefined,
    {
      queryKey: ['interactive-schema-reports', 'main-component', mainComponentId],
      enabled: !!mainComponentId,
    },
  )
